import CoreUtils from '../utils/coreUtils';
import VoxelGrid from './nodeGraph/voxelGrid';
import Utils3D from './utils/utils3D.js';

export default class ShortestPathFinder
{
	static findShortestPath3D(vecStart, vecEnd, pathInfos, excludedIds, vertexEqualsEpsilon = 1e-4)
	{
		const allPoints = pathInfos.flatMap(pathInfo => { return [pathInfo.points[0], pathInfo.points[1]]; });
		let bbox = Utils3D.computePointsBBox(allPoints);

		// Build our vertices with connectivity info
		let voxelGridStart = new BABYLON.Vector3(bbox.min.x, bbox.min.y, bbox.min.z);
		let voxelGridEnd = new BABYLON.Vector3(bbox.max.x, bbox.min.y, bbox.max.z);
		let vertexGrid = new VoxelGrid(voxelGridStart, voxelGridEnd, 64);

		let findExistingVertex = (vecPoint) => {	
			let vertices = vertexGrid.getObjects(vecPoint);
			for(let currVert of vertices)
			{
				if(BABYLON.Vector3.Distance(currVert.point, vecPoint) < vertexEqualsEpsilon)
					return currVert;
			}
			return null;
		};

		let allVertices = [];
		let iNumStartVerticesFound = 0;
		const iNumLines = pathInfos.length;
		for(let iLine = 0; iLine < iNumLines; iLine++)
		{
			let pathInfo = pathInfos[iLine];
			if(excludedIds && excludedIds.has(pathInfo.id))
				continue;

			// Skip the start-end segment, otherwise we'd find it as the closest path, of course. :)
			let pathPoints = pathInfo.points;
			if(Utils3D.areSegmentsIdentical(pathPoints[0], pathPoints[1], vecStart, vecEnd, vertexEqualsEpsilon))
				continue;

			// See if its vertices already exist:
			let vertInfos = [];
			for(let pointIndex = 0; pointIndex < pathPoints.length; pointIndex++)
			{
				let vertInfo = findExistingVertex(pathPoints[pointIndex]);
				if(!vertInfo)
				{
					vertInfo = {
						point: pathPoints[pointIndex],
						dist: Number.MAX_VALUE,
						neighbours: [],
						weights: [],
						pathIds: new Set(),
						prev: null,
					};

					// Mark start point as zero distance
					if(BABYLON.Vector3.Distance(vertInfo.point, vecStart) < vertexEqualsEpsilon) {
						vertInfo.dist = 0;
						iNumStartVerticesFound++;
					}

					vertexGrid.addObject(vertInfo.point, vertInfo);
					allVertices.push(vertInfo);
				}
				vertInfos[pointIndex] = vertInfo;
			}

			vertInfos[0].neighbours.push(vertInfos[1]);
			vertInfos[1].neighbours.push(vertInfos[0]);

			vertInfos[0].pathIds.add(pathInfo.id);
			vertInfos[1].pathIds.add(pathInfo.id);

			const pathWeight = pathInfo.weight === undefined ? 1.0 : pathInfo.weight;
			vertInfos[0].weights.push(pathWeight);
			vertInfos[1].weights.push(pathWeight);
		}

		if(iNumStartVerticesFound === 0)
		{
			// This likely means the epsilon value for considering vertices the same
			// here needs to be increased.
			console.warn(`Loop detection: no start vertex found`);
			return { points: null };
		}
		else if(iNumStartVerticesFound > 1)
		{
			// Several vertices match the end vertex. Results may be unpredictable.
			console.warn(`Loop detection: too many start vertices found`);
		}

		// Find shortest path
		let targetVertexInfo;
		while(allVertices.length > 0)
		{
			// Find smallest value:
			let resVertIndex = 0;
			let minDist = Number.MAX_VALUE;
			for(let idx = allVertices.length - 1; idx >= 0; idx--)
			{
				let vert = allVertices[idx];
				if(vert.dist <= minDist)
				{
					minDist = vert.dist;
					resVertIndex = idx;
				}
			}

			let minDistVert = allVertices[resVertIndex];
			if(BABYLON.Vector3.Distance(vecEnd, minDistVert.point) < vertexEqualsEpsilon)
			{
				targetVertexInfo = minDistVert;
				break;
			}

			minDistVert.deleted = true;			
			allVertices.splice(resVertIndex, 1);
			
			for(let neighbourIdx = 0; neighbourIdx < minDistVert.neighbours.length; neighbourIdx++)
			{
				const neighbour = minDistVert.neighbours[neighbourIdx];
				if(neighbour.deleted)
					continue;

				let edgeLength = BABYLON.Vector3.Distance(neighbour.point, minDistVert.point)*minDistVert.weights[neighbourIdx];
				let currDist = minDistVert.dist + edgeLength;
				if(currDist < neighbour.dist)
				{
					neighbour.dist = currDist;
					neighbour.prev = minDistVert;
				}
			}
		}

		let idsUsed = [];
		let fullPath = [];
		let currInfo = targetVertexInfo;
		while(currInfo && currInfo.prev)
		{
			// Find path ids used:
			if(currInfo.prev)
			{
				let usedPaths = CoreUtils.getIntersection(currInfo.pathIds, currInfo.prev.pathIds);
				usedPaths.forEach((pathId) => { idsUsed.push(pathId); });
			}

			fullPath.push(currInfo.point);
			currInfo = currInfo.prev;
		}

		fullPath.push(vecStart);
		fullPath.reverse();
		if(fullPath.length <= 1)
			return { points: null };

		return { points: fullPath, idsUsed: idsUsed };
	}
}